/**
 * ============================================================
 * 结算报表页面 (Report) - v1
 * 功能：
 *   - 管理员可以看到所有经纪人的结算报表
 *   - 经纪人只能看到自己的结算报表
 *   - 根据经纪人绑定的账单模板显示字段
 *   - 根据经纪人绑定的分成公式计算分成
 * ============================================================
 */

import { useEffect, useState, useCallback } from 'react';
import { reportApi, formulaApi, brokerApi } from '../api';
import { useAuth } from '../auth';
import { BarChart3, Calendar, User, ChevronRight, Eye } from 'lucide-react';

// ===== 中文字段标签映射 =====
const FIELD_LABELS: Record<string, string> = {
  '结算月份': 'settlement_month', '主播昵称': 'anchor_name', '主播uid': 'anchor_uid',
  '主播房间号': 'anchor_room_id', '入会时间': 'join_date', '首次开播时间': 'first_stream_date',
  '经纪人昵称': 'broker_name', '经纪人uid': 'broker_uid',
  '招募经纪人昵称': 'recruit_broker_name', '招募经纪人uid': 'recruit_broker_uid',
  '主播支付': 'anchor_payment', '主播总激励': 'anchor_total_incentive',
  '主播非自提礼物收益': 'anchor_non_self_gift_income', '非自提合计底薪': 'non_self_base_salary',
  '合计活动收益': 'total_activity_income', '非自提运营奖励': 'non_self_operation_reward',
  '非自提运营惩罚': 'non_self_operation_penalty', '主播负向治理收益总扣除': 'governance_deduction',
  '主播负向治理收益总补回': 'governance_recovery', '开播时长(含连麦时长)': 'stream_duration',
  '有效开播天': 'valid_stream_days', '有效开播时长': 'valid_stream_hours',
  '是否入会无首播主播': 'is_no_first_stream', '是否TOP主播': 'is_top_anchor',
  'TOPstar主播评级': 'topstar_level', '总流水': 'total_stream',
  '是否是政策新主播': 'is_policy_new_anchor', '新主播任务流水': 'new_anchor_task_stream',
  '新主播激励分成': 'new_anchor_incentive_share', '新主播激励': 'new_anchor_incentive',
  '新主播结算周期': 'new_anchor_settlement_period', '直播有效天/新主播周期': 'valid_days_in_period',
  '直播有效时长/新主播周期': 'valid_hours_in_period', '当月新主播流水': 'current_month_new_anchor_stream',
  '次月待结算新主播流水': 'next_month_pending_new_anchor_stream', '老主播任务流水': 'old_anchor_task_stream',
  '老主播任务分成': 'old_anchor_task_share', '老主播任务激励': 'old_anchor_task_incentive',
  'TOP主播任务分成': 'top_anchor_task_share', 'TOP主播任务激励': 'top_anchor_task_incentive',
  '成长任务分成': 'growth_task_share', '成长任务激励': 'growth_task_incentive',
  '流水任务分成': 'stream_task_share', '流水任务激励': 'stream_task_incentive',
  '优质奖励': 'quality_reward', '未成年扣除': 'minor_deduction',
};

const DB_TO_LABEL: Record<string, string> = {};
for (const [label, dbField] of Object.entries(FIELD_LABELS)) DB_TO_LABEL[dbField] = label;

const STRING_FIELDS = new Set([
  'settlement_month', 'anchor_name', 'anchor_uid', 'anchor_room_id',
  'join_date', 'first_stream_date', 'broker_name', 'broker_uid',
  'recruit_broker_name', 'recruit_broker_uid',
  'is_no_first_stream', 'is_top_anchor', 'topstar_level',
  'is_policy_new_anchor', 'new_anchor_settlement_period',
]);



interface TemplateColumn { fieldKey: string; displayName: string; width: number; visible: boolean; }

interface BrokerInfo {
  id: number;
  name: string;
  uid?: string;
  type?: string;
  formulaId?: number;
  formulaName?: string;
  templateId?: number;
  templateName?: string;
  boundUserId?: number;
  boundUserName?: string;
}

interface ReportData {
  broker: BrokerInfo;
  templateColumns: TemplateColumn[];
  anchors: any[];
  totalAmount: number;
  formulaDetails: Record<string, number>;
}

export default function ReportPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState<BrokerInfo[]>([]);
  const [selectedBroker, setSelectedBroker] = useState<string>('');
  const [months, setMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [expandedBroker, setExpandedBroker] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showSuccess = useCallback((m: string) => { setToast({ message: m, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((m: string) => { setToast({ message: m, type: 'error' }); setTimeout(() => setToast(null), 5000); }, []);

  // ===== 加载经纪人列表 =====
  useEffect(() => {
    loadBrokers();
  }, []);

  const loadBrokers = async () => {
    try {
      const brokerList = await brokerApi.list();
      console.log('完整经纪人列表:', brokerList);
      setBrokers(brokerList);
      
      // 调试信息
      console.log('isAdmin:', isAdmin);
      console.log('user:', user);
      console.log('当前登录用户:', user?.name);
      console.log('经纪人列表:', brokerList.map(b => b.name));
      
      // 只有当经纪人列表中确实存在当前用户时，才自动选择
      if (!isAdmin && user) {
        // 从用户信息中获取绑定的经纪人名称
        const userBoundBroker = (user as any)?.bound_broker_name;
        console.log('用户绑定的经纪人名称:', userBoundBroker);
        
        // 根据用户绑定的经纪人名称查找对应的经纪人
        const currentBroker = brokerList.find(b => b.name === userBoundBroker);
        
        if (currentBroker) {
          console.log('找到用户绑定的经纪人信息，自动选择:', currentBroker.name);
          setSelectedBroker(currentBroker.name);
          // 打印经纪人的公式和模板信息
          console.log('经纪人公式:', currentBroker.formulaName || currentBroker.formulaId);
          console.log('经纪人模板:', currentBroker.templateName || currentBroker.templateId);
          console.log('经纪人绑定用户:', (currentBroker as any).bound_user_name || (currentBroker as any).boundUserName);
        } else {
          console.log('经纪人列表中没有找到当前用户:', user.name);
          console.log('经纪人列表详情:', brokerList.map(b => ({
            name: b.name,
            bound_user_name: (b as any).bound_user_name,
            boundUserName: (b as any).boundUserName
          })));
          // 不自动选择，让用户手动选择
          setSelectedBroker('');
        }
      }
    } catch (e) {
      console.error('加载经纪人失败', e);
      showError('加载经纪人失败');
    } finally {
      setLoading(false);
    }
  };

  // ===== 选择经纪人后加载月份 =====
  useEffect(() => {
    // 只使用从经纪人列表中选择的真实经纪人
    if (selectedBroker) {
      console.log('选择的经纪人:', selectedBroker);
      
      // 获取经纪人信息，打印公式和模板
      const currentBroker = brokers.find(b => b.name === selectedBroker);
      if (currentBroker) {
        console.log('经纪人公式:', currentBroker.formulaName || currentBroker.formulaId);
        console.log('经纪人模板:', currentBroker.templateName || currentBroker.templateId);
      }
      
      // 获取真实的月份列表
      formulaApi.brokerMonths(selectedBroker).then(months => {
        console.log('获取到的月份列表:', months);
        setMonths(months);
        
        if (!selectedMonth) {
          if (months.length > 0) {
            // 选择最新的月份（按降序排列后取第一个）
            const sortedMonths = [...months].sort((a, b) => b.localeCompare(a));
            setSelectedMonth(sortedMonths[0]);
            console.log('默认选择最新月份:', sortedMonths[0]);
          }
        }
      }).catch(err => {
        console.error('获取月份列表失败:', err);
        setMonths([]);
      });
    } else {
      console.log('没有选择经纪人');
      setMonths([]);
      setSelectedMonth('');
    }
  }, [selectedBroker, brokers]);

  // ===== 加载报表数据 =====
  const loadReportData = async () => {
    if (!selectedBroker || !selectedMonth) {
      console.log('缺少必要参数:', { selectedBroker, selectedMonth });
      return;
    }

    try {
      console.log('进入loadReportData函数');
      // 设置加载状态
      setLoading(true);
      // 清除之前的报表数据
      setReportData(null);
      // 1. 获取经纪人信息（必须使用真实的经纪人数据）
      const broker = brokers.find(b => b.name === selectedBroker);
      if (!broker) {
        console.error('经纪人不存在:', selectedBroker);
        showError('经纪人不存在');
        return;
      }
      console.log('获取到经纪人信息:', broker);
      
      // 2. 获取主播数据
      console.log('准备获取主播数据，参数:', { brokerName: selectedBroker, month: selectedMonth });
      let anchors = await formulaApi.brokerMonthAnchors(selectedBroker, selectedMonth);
      console.log('获取到主播数据数量:', anchors.length);
      if (anchors.length > 0) {
        console.log('第一个主播数据:', anchors[0]);
      }

      // 3. 获取经纪人的公式和模板
      let templateColumns: TemplateColumn[] = [];
      let totalAmount = 0;
      console.log('经纪人公式ID:', broker.formulaId);
      console.log('经纪人模板ID:', broker.templateId);

      // 如果经纪人有公式，计算每个主播的分成
      let formulaDetails: Record<string, number> = {};
      if (broker.formulaId) {
        const formulaList = await formulaApi.list();
        const formula = formulaList.find(f => f.id === broker.formulaId);
        if (formula) {
          console.log('获取到公式:', formula);
          console.log('准备测试公式，参数:', { expression: formula.expression, brokerName: selectedBroker, month: selectedMonth });
          // 如果经纪人有公式，计算每个主播的分成
          const testResult = await formulaApi.test(
            formula.expression,
            undefined,
            selectedBroker,
            selectedMonth
          );
          totalAmount = Number(testResult.result) || 0;
          
          // 将每个主播的分成金额合并到主播数据中
          if (testResult.details && testResult.details.length > 0) {
            const anchorResults = new Map();
            testResult.details.forEach(detail => {
              anchorResults.set(detail.anchorName, detail.result);
            });
            
            // 为每个主播添加divided_amount字段
            anchors = anchors.map(anchor => ({
              ...anchor,
              divided_amount: anchorResults.get(anchor.anchor_name) || 0
            }));
          }
          
          // 使用后端返回的字段计算结果
          if (testResult.fieldResults) {
            console.log('后端返回的字段结果:', testResult.fieldResults);
            formulaDetails = testResult.fieldResults;
          }
        }
      }

      // 如果经纪人有模板，获取模板配置
      if (broker.templateId) {
        console.log('经纪人有模板，准备获取模板列表');
        const templateList = await reportApi.templates();
        console.log('获取到模板列表数量:', templateList.length);
        const template = templateList.find(t => t.id === broker.templateId);
        if (template) {
          console.log('获取到模板:', template);
          if (template?.config?.columns) {
            templateColumns = template.config.columns;
            console.log('模板配置列:', templateColumns);
          } else {
            console.log('模板没有配置列:', template.config);
          }
        } else {
          console.log('没有找到匹配的模板:', broker.templateId);
        }
      } else {
        console.log('经纪人没有模板');
      }

      // 默认模板（如果没有绑定模板）
      if (templateColumns.length === 0) {
        templateColumns = [
          { fieldKey: 'anchor_name', displayName: '主播昵称', width: 150, visible: true },
          { fieldKey: 'anchor_uid', displayName: '主播uid', width: 120, visible: true },
          { fieldKey: 'total_stream', displayName: '总流水', width: 120, visible: true },
          { fieldKey: 'new_anchor_task_stream', displayName: '新主播任务流水', width: 150, visible: true },
          { fieldKey: 'old_anchor_task_stream', displayName: '老主播任务流水', width: 150, visible: true },
        ];
      }
      
      // 确保分成金额列始终显示
      if (!templateColumns.some(col => col.fieldKey === 'divided_amount')) {
        templateColumns.push({
          fieldKey: 'divided_amount',
          displayName: '分成金额',
          width: 150,
          visible: true
        });
      }
      console.log('最终模板列:', templateColumns);
      
      // 准备报表数据
      const reportData: ReportData = {
        broker,
        month: selectedMonth,
        anchors,
        templateColumns,
        totalAmount,
        formulaDetails
      };
      
      console.log('最终报表数据:', reportData);
      setReportData(reportData);

      // 重置加载状态
      setLoading(false);
    } catch (e) {
      console.error('加载报表数据失败', e);
      showError('加载报表数据失败');
    }
  };

  // ===== 当选择月份或经纪人变化时加载数据 =====
  useEffect(() => {
    console.log('selectedBroker变化:', selectedBroker);
    console.log('selectedMonth变化:', selectedMonth);
    if (selectedBroker && selectedMonth) {
      console.log('准备加载报表数据:', { selectedBroker, selectedMonth });
      loadReportData();
    } else {
      console.log('缺少必要参数，不加载数据:', { selectedBroker, selectedMonth });
    }
  }, [selectedBroker, selectedMonth]);

  const getDisplayValue = (row: any, fieldKey: string): string => {
    if (!row) return '-';
    const val = row[fieldKey];
    if (val === undefined || val === null || val === '') return '-';
    if (!STRING_FIELDS.has(fieldKey)) {
      const n = Number(val);
      if (isNaN(n)) return String(val);
      // 如果是分成金额字段，添加￥符号
      if (fieldKey === 'divided_amount') {
        return `¥${n.toLocaleString()}`;
      }
      return n.toLocaleString();
    }
    return String(val);
  };

  // ===== 解析公式字段并计算明细 =====
  const calculateFormulaDetails = (formulaExpression: string, anchors: any[]) => {
    // 提取公式中的所有字段
    const fieldMatches = formulaExpression.match(/\[([^\]]+)\]/g) || [];
    const fieldLabels = [...new Set(fieldMatches.map(m => m.slice(1, -1)))];
    
    // 计算公式中所有字段的总和（不再过滤，显示所有字段）
    const fieldSums = new Map<string, number>();
    fieldLabels.forEach(label => {
      // 查找对应的数据库字段名
      const dbField = FIELD_LABELS[label] || label;
      const sum = anchors.reduce((total, anchor) => {
        return total + Number(anchor[dbField] || 0);
      }, 0);
      fieldSums.set(label, sum);
    });
    
    return fieldSums;
  };

  // ===== 过滤可见的经纪人 =====
  // 管理员和财务角色可以查看所有经纪人，普通用户只能看到自己绑定的经纪人
  const isFinance = user?.role?.toLowerCase() === 'finance' || user?.role === '财务';
  const canViewAll = isAdmin || isFinance;
  
  const visibleBrokers = canViewAll ? brokers : (
    brokers.filter(b => {
      // 从用户信息中获取绑定的经纪人名称
      const userBoundBroker = (user as any)?.bound_broker_name;
      console.log('用户绑定经纪人:', userBoundBroker);
      console.log('经纪人信息:', { name: b.name, id: b.id });
      
      // 检查经纪人名称是否与用户绑定的经纪人名称匹配
      return b.name === userBoundBroker;
    })
  );
  console.log('可见的经纪人:', visibleBrokers);
  console.log('用户绑定经纪人名称:', (user as any)?.bound_broker_name);
  console.log('所有经纪人列表:', brokers);
  console.log('当前角色:', user?.role, 'isAdmin:', isAdmin, 'isFinance:', isFinance, 'canViewAll:', canViewAll);

  // ===== 当可见经纪人变化时自动选择第一个 =====
  useEffect(() => {
    if (!isAdmin && visibleBrokers.length > 0 && !selectedBroker) {
      setSelectedBroker(visibleBrokers[0].name);
      console.log('从可见经纪人中自动选择:', visibleBrokers[0].name);
    }
  }, [visibleBrokers, isAdmin, selectedBroker]);

  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-[#F59E0B]" />
          结算报表
        </h1>
      </div>

      {/* 筛选条件 */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* 经纪人选择（仅管理员） */}
          {isAdmin && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-[#94A3B8] mb-1.5 block flex items-center gap-1">
                <User className="w-3 h-3" />
                选择经纪人
              </label>
              <select
                value={selectedBroker}
                onChange={e => setSelectedBroker(e.target.value)}
                className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]"
              >
                <option value="">请选择经纪人</option>
                {visibleBrokers.map(b => (
                  <option key={b.id} value={b.name}>{b.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* 月份选择 */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-[#94A3B8] mb-1.5 block flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              选择月份
            </label>
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              disabled={!selectedBroker}
              className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9] disabled:opacity-50"
            >
              <option value="">请选择月份</option>
              {/* 只显示从API获取的真实月份列表 */}
              {months.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 报表数据 */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reportData ? (
        <div>
          {/* 经纪人信息和合计 */}
          <div className="bg-[#1E293B] border border-[#334155] border-b-0 rounded-t-xl p-4 sticky top-0 z-20">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              {/* 经纪人基本信息 */}
              <div>
                <h3 className="text-lg font-medium text-[#F1F5F9]">{reportData.broker.name}</h3>
                <p className="text-xs text-[#94A3B8] mt-1">
                  公式: {reportData.broker.formulaName || '未绑定'} | 
                  模板: {reportData.broker.templateName || '未绑定'}
                </p>
              </div>
              
              {/* 右侧：公式明细 + 合计分成 - 水平排列 */}
              <div className="flex items-end gap-4 flex-wrap">
                {/* 公式明细 - 水平排列 */}
                {Object.keys(reportData.formulaDetails).length > 0 && (
                  <div className="flex items-end gap-4">
                    {Object.entries(reportData.formulaDetails).map(([field, amount]) => (
                      <div key={field} className="text-right flex flex-col items-end">
                        <span className="text-xs text-[#94A3B8]">{field}</span>
                        <span className="text-2xl font-bold text-emerald-400">
                          ¥{amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* 合计分成 - 与公式明细在同一行 */}
                <div className="text-right border-l border-[#334155] pl-4">
                  <p className="text-xs text-[#94A3B8]">合计分成</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    ¥{reportData.totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 主播明细表格 */}
          <div className="bg-[#1E293B] border border-[#334155] border-t-0 rounded-b-xl overflow-hidden">
            {/* 主播明细标题栏 - 显示数据数量 */}
            <div className="px-4 py-3 bg-[#0F172A] border-b border-[#334155] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#3B82F6]" />
                <span className="text-sm font-medium">主播明细</span>
                <span className="px-2 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded-full text-xs">
                  {reportData.anchors.length}条
                </span>
              </div>
            </div>
            
            {/* 可滚动区域 - 支持水平和垂直滚动 */}
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-510px)]">
              <table className="w-full text-xs">
                {/* 表头 - 固定 */}
                <thead className="bg-[#0F172A]">
                  <tr>
                    {reportData.templateColumns.filter(c => c.visible).map((col, index) => (
                      <th
                        key={col.fieldKey}
                        className={`px-3 py-2 text-left font-medium border-b border-[#334155] whitespace-nowrap ${col.fieldKey === 'divided_amount' ? 'text-[#F59E0B]' : 'text-[#94A3B8]'}`}
                        style={{
                          width: index === 0 ? '70px' : index === 1 ? '140px' : col.width,
                          minWidth: index === 0 ? '70px' : index === 1 ? '140px' : col.width,
                          position: 'sticky',
                          top: 0,
                          left: index === 0 ? 0 : index === 1 ? '70px' : 'auto',
                          backgroundColor: '#0F172A',
                          zIndex: index < 2 ? 40 : 30
                        }}
                      >
                        {col.displayName}
                      </th>
                    ))}
                  </tr>
                </thead>
                
                {/* 数据行 - 可滚动 */}
                <tbody>
                  {reportData.anchors.length > 0 ? (
                    reportData.anchors.map((row, idx) => (
                      <tr key={idx} className="hover:bg-[#1E293B]/50 border-b border-[#334155]/30">
                        {reportData.templateColumns.filter(c => c.visible).map((col, index) => (
                          <td
                            key={col.fieldKey}
                            className={`px-3 py-2 whitespace-nowrap ${col.fieldKey === 'divided_amount' ? 'text-[#F59E0B] font-medium' : 'text-[#F1F5F9]'}`}
                            style={{
                              width: index === 0 ? '70px' : index === 1 ? '140px' : col.width,
                              minWidth: index === 0 ? '70px' : index === 1 ? '140px' : col.width,
                              position: index < 2 ? 'sticky' : 'static',
                              left: index === 0 ? 0 : index === 1 ? '70px' : 'auto',
                              backgroundColor: '#1E293B',
                              zIndex: 20
                            }}
                          >
                            {getDisplayValue(row, col.fieldKey)}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={reportData.templateColumns.filter(c => c.visible).length}
                        className="px-3 py-8 text-[#64748B] text-center"
                      >
                        暂无数据
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-12 text-center">
          <BarChart3 className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
          <p className="text-[#94A3B8]">请选择经纪人和月份查看报表</p>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
